import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, apiGet, apiSend, serverBesked } from "../../lib/customFetch";
import styles from "./AbsenceRequests.module.css";

const requestSchema = z
  .object({
    id: z.uuid(),
    type: z.enum(["ferie", "sygdom", "fri"]),
    fraDato: z.iso.date(),
    tilDato: z.iso.date(),
    note: z.string().nullable(),
    status: z.enum(["afventer", "godkendt", "afslaaet", "annulleret"]),
    afgoerelseNote: z.string().nullable(),
    afgjortAt: z.iso.datetime({ offset: true }).nullable(),
    annulleretAt: z.iso.datetime({ offset: true }).nullable(),
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
const requestsSchema = z.array(requestSchema);

type AbsenceRequest = z.infer<typeof requestSchema>;
type AbsenceType = AbsenceRequest["type"];

const TYPE_LABEL: Record<AbsenceType, string> = {
  ferie: "Ferie",
  sygdom: "Sygdom",
  fri: "Fri",
};
const STATUS_LABEL: Record<AbsenceRequest["status"], string> = {
  afventer: "Afventer godkendelse",
  godkendt: "Godkendt",
  afslaaet: "Afslået",
  annulleret: "Annulleret",
};
const dateFormat = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

function formatDate(value: string): string {
  return dateFormat.format(new Date(`${value}T00:00:00Z`));
}

function intervalDays(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  return Math.floor((end - start) / 86_400_000) + 1;
}

function requestProblem(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return serverBesked(error) ?? fallback;
  return fallback;
}

export function AbsenceRequests({
  variant = "light",
}: {
  variant?: "light" | "dark";
}) {
  const [requests, setRequests] = useState<AbsenceRequest[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [type, setType] = useState<AbsenceType>("ferie");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const fromRef = useRef<HTMLInputElement>(null);
  const createClientId = useRef(crypto.randomUUID());

  const load = useCallback(() => {
    setLoadError(false);
    setConfirmation(null);
    apiGet<unknown>("/api/clockin/absence-requests")
      .then((payload) => {
        setRequests(requestsSchema.parse(payload));
      })
      .catch(() => {
        setRequests(null);
        setLoadError(true);
      });
  }, []);

  useEffect(() => load(), [load]);
  useEffect(() => {
    if (formOpen) fromRef.current?.focus();
  }, [formOpen]);

  const resetForm = () => {
    setType("ferie");
    setFromDate("");
    setToDate("");
    setNote("");
    setFormError(null);
  };

  const submit = () => {
    const days = intervalDays(fromDate, toDate);
    if (fromDate === "" || toDate === "" || days < 1 || days > 31) {
      setFormError("Vælg en periode på 1-31 kalenderdage.");
      return;
    }
    setBusy(true);
    setFormError(null);
    setConfirmation(null);
    apiSend<unknown>("POST", "/api/clockin/absence-requests", {
      clientId: createClientId.current,
      type,
      fraDato: fromDate,
      tilDato: toDate,
      note: note.trim() === "" ? undefined : note.trim(),
    }, { signal: AbortSignal.timeout(15_000) })
      .then((payload) => {
        const created = requestSchema.parse(payload);
        setRequests((current) => [created, ...(current ?? [])]);
        setLoadError(false);
        setConfirmation("Din anmodning er sendt til godkendelse.");
        createClientId.current = crypto.randomUUID();
        setFormOpen(false);
        resetForm();
      })
      .catch((error: unknown) =>
        setFormError(requestProblem(error, "Kunne ikke sende anmodningen — prøv igen.")),
      )
      .finally(() => setBusy(false));
  };

  const cancel = (id: string) => {
    setBusy(true);
    setCancelError(null);
    apiSend<unknown>(
      "DELETE",
      `/api/clockin/absence-requests/${id}`,
      undefined,
      { signal: AbortSignal.timeout(15_000) },
    )
      .then((payload) => {
        const updated = requestSchema.parse(payload);
        setRequests((current) =>
          current?.map((request) => (request.id === id ? updated : request)) ?? [],
        );
        setCancelId(null);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && (error.status === 404 || error.status === 409)) {
          setCancelId(null);
          load();
        }
        setCancelError(requestProblem(error, "Kunne ikke annullere — prøv igen."));
      })
      .finally(() => setBusy(false));
  };

  return (
    <section
      className={styles.panel}
      data-variant={variant}
      aria-labelledby="absence-requests-title"
    >
      <div className={styles.headingRow}>
        <div>
          <h2 id="absence-requests-title">Fravær</h2>
          <p>Send en anmodning til din leder. Den registreres først efter godkendelse.</p>
        </div>
        <div className={styles.headingActions}>
          <button type="button" className={styles.refreshButton} onClick={load}>
            Opdatér
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            aria-expanded={formOpen}
            aria-controls="absence-request-form"
            onClick={() => {
              setFormOpen((open) => !open);
              setFormError(null);
            }}
          >
            {formOpen ? "Luk" : "Anmod om fravær"}
          </button>
        </div>
      </div>

      {formOpen && (
        <div id="absence-request-form" className={styles.form}>
          <label>
            Type
            <select value={type} onChange={(event) => setType(event.target.value as AbsenceType)}>
              <option value="ferie">Ferie</option>
              <option value="sygdom">Sygdom</option>
              <option value="fri">Fri</option>
            </select>
          </label>
          <div className={styles.dateRow}>
            <label>
              Fra
              <input
                ref={fromRef}
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event.target.value);
                  if (toDate === "") setToDate(event.target.value);
                }}
              />
            </label>
            <label>
              Til
              <input
                type="date"
                min={fromDate}
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
              />
            </label>
          </div>
          <label>
            Note <span>(valgfri)</span>
            <textarea
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </label>
          {formError !== null && <p className={styles.error} role="alert">{formError}</p>}
          <button
            type="button"
            className={styles.submitButton}
            disabled={busy}
            onClick={submit}
          >
            {busy ? "Sender…" : "Send anmodning"}
          </button>
        </div>
      )}

      {confirmation !== null && (
        <p className={styles.confirmation} role="status">{confirmation}</p>
      )}

      {loadError ? (
        <div className={styles.state} role="alert">
          <p>Kunne ikke hente dine anmodninger.</p>
          <button type="button" onClick={load}>Prøv igen</button>
        </div>
      ) : requests === null ? (
        <p className={styles.state} role="status">Henter anmodninger…</p>
      ) : requests.length === 0 ? (
        <p className={styles.state}>Du har ingen fraværsanmodninger endnu.</p>
      ) : (
        <ul className={styles.list} aria-label="Dine fraværsanmodninger">
          {requests.map((request) => (
            <li key={request.id} className={styles.request}>
              <div className={styles.requestTop}>
                <strong>{TYPE_LABEL[request.type]}</strong>
                <span className={styles.status} data-status={request.status}>
                  {STATUS_LABEL[request.status]}
                </span>
              </div>
              <p className={styles.interval}>
                {formatDate(request.fraDato)}
                {request.fraDato === request.tilDato
                  ? ""
                  : ` – ${formatDate(request.tilDato)}`}
              </p>
              {request.note !== null && <p className={styles.note}>{request.note}</p>}
              {request.afgoerelseNote !== null && (
                <p className={styles.decisionNote}>Lederens note: {request.afgoerelseNote}</p>
              )}
              {request.status === "afventer" &&
                (cancelId === request.id ? (
                  <div className={styles.confirm} role="group" aria-label="Bekræft annullering">
                    <span>Vil du annullere anmodningen?</span>
                    <button type="button" disabled={busy} onClick={() => cancel(request.id)}>
                      Ja, annullér
                    </button>
                    <button type="button" onClick={() => setCancelId(null)}>
                      Behold
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setCancelId(request.id);
                      setCancelError(null);
                    }}
                  >
                    Annuller anmodning
                  </button>
                ))}
              {cancelId === request.id && cancelError !== null && (
                <p className={styles.error} role="alert">{cancelError}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
